import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CreditCard, MessageCircle, ShieldCheck, Check } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { fmt, currency, country, symbol, shippingBase } = useCurrency();
  const navigate = useNavigate();
  const [config, setConfig] = useState({ whatsapp_number: "", paystack_enabled: false });
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", address: "", city: "", state: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const shipping = items.length > 0 ? shippingBase : 0;
  const total = subtotal + shipping;

  // Hoodie price match: ₦25,000 NG · $35/CA$35/£35/€35/AU$35/NZ$35 elsewhere.
  const isHoodie = (i) => /hoodie/i.test(i?.name || "");
  const money = (v) => (currency === "NGN" ? fmt(v) : country === "AU" ? `AU$${v}` : country === "NZ" ? `NZ$${v}` : `${symbol}${v}`);
  const priceFmt = (i, qty = 1) => (isHoodie(i) ? money((currency === "NGN" ? 25000 : 35) * qty) : fmt(i.price * qty));
  const cartSubtotal = items.reduce((s, i) => s + (isHoodie(i) ? (currency === "NGN" ? 25000 : 35) : currency === "NGN" ? i.price : i.price / 1000) * i.quantity, 0);
  const shipDisplay = currency === "NGN" ? shipping : shipping / 1000;
  const totalDisplay = cartSubtotal + shipDisplay;

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    for (const k of ["customer_name", "email", "phone", "address", "city", "state"]) {
      if (!form[k].trim()) { toast.error("Please fill in all delivery details"); return false; }
    }
    return true;
  };

  const createOrder = async (method) => {
    const payload = {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size })),
      ...form, method, currency,
    };
    const { data } = await api.post("/orders", payload);
    return data; // {reference, currency, symbol, subtotal, shipping, total}
  };

  const payWithPaystack = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const order = await createOrder("paystack");
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/checkout/paystack/initialize", {
        reference: order.reference, email: form.email, callback_url,
      });
      window.location.href = data.authorization_url;
    } catch (e) {
      const msg = e.response?.data?.detail || "Payment could not be started.";
      toast.error(typeof msg === "string" ? msg : "Payment error");
      setLoading(false);
    }
  };

  const orderViaWhatsApp = async () => {
    if (!validate()) return;
    if (!config.whatsapp_number) { toast.error("WhatsApp checkout unavailable."); return; }
    setLoading(true);
    try {
      const order = await createOrder("whatsapp");
      const lines = items.map((i) => `• ${i.name}${i.size ? ` (${i.size})` : ""} x${i.quantity} — ${priceFmt(i, i.quantity)}`).join("\n");
      const msg =
        `*New NAIJAMAN Order* (${order.currency})\nRef: ${order.reference}\n\n${lines}\n\n` +
        `Subtotal: ${money(cartSubtotal)}\nShipping: ${money(shipDisplay)}\n*Total: ${money(totalDisplay)}*\n\n` +
        `*Deliver to:*\n${form.customer_name}\n${form.phone}\n${form.address}, ${form.city}, ${form.state}`;
      const url = `https://wa.me/${config.whatsapp_number}?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank");
      clear();
      setDone(order.reference);
    } catch {
      toast.error("Could not place order. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#050806] grid place-items-center p-6" data-testid="checkout-success">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto grid place-items-center rounded-full bg-emerald-500 text-emerald-950"><Check size={32} /></div>
          <h1 className="font-display text-4xl text-white mt-5">ORDER SENT!</h1>
          <p className="text-gray-300 mt-2">Your order <span className="text-emerald-400 font-mono">{done}</span> has been sent to our team on WhatsApp. We&rsquo;ll confirm payment and delivery shortly.</p>
          <Link to="/merch" className="inline-block btn-skew bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-accent text-xl tracking-wide px-8 py-3 mt-7" data-testid="success-continue-btn"><span>Continue Shopping</span></Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#050806] grid place-items-center p-6" data-testid="checkout-empty">
        <div className="text-center">
          <h1 className="font-display text-4xl text-white">YOUR CART IS EMPTY</h1>
          <Link to="/merch" className="inline-block btn-skew bg-emerald-500 text-emerald-950 font-accent text-xl px-8 py-3 mt-6"><span>Browse the Store</span></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050806] text-white" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <Link to="/merch" className="inline-flex items-center gap-2 text-gray-400 hover:text-emerald-400 mb-8 font-accent tracking-wider"><ArrowLeft size={16} /> BACK TO STORE</Link>
        <h1 className="font-display text-5xl mb-8">CHECKOUT</h1>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Delivery form */}
          <div className="lg:col-span-3 space-y-4">
            <h2 className="font-accent text-2xl tracking-wider text-emerald-400">DELIVERY DETAILS</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Full Name" value={form.customer_name} onChange={set("customer_name")} testid="checkout-name" />
              <Input label="Email" type="email" value={form.email} onChange={set("email")} testid="checkout-email" />
              <Input label="Phone" value={form.phone} onChange={set("phone")} testid="checkout-phone" />
              <Input label="City" value={form.city} onChange={set("city")} testid="checkout-city" />
              <Input label="State" value={form.state} onChange={set("state")} testid="checkout-state" />
            </div>
            <Input label="Delivery Address" value={form.address} onChange={set("address")} testid="checkout-address" />
          </div>

          {/* Summary */}
          <div className="lg:col-span-2">
            <div className="rounded-xl border border-white/10 bg-white/5 p-6 sticky top-6">
              <h2 className="font-accent text-2xl tracking-wider text-white mb-4">ORDER SUMMARY</h2>
              <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                {items.map((i) => (
                  <div key={`${i.product_id}-${i.size}`} className="flex gap-3 items-center">
                    <img src={i.image} alt={i.name} className="w-12 h-12 rounded object-cover bg-white" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{i.name}</p>
                      <p className="text-xs text-gray-400">{i.size ? `${i.size} · ` : ""}Qty {i.quantity}</p>
                    </div>
                    <span className="text-sm text-emerald-400">{priceFmt(i, i.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 pt-4 space-y-1 text-sm">
                <div className="flex justify-between text-gray-300"><span>Subtotal</span><span>{money(cartSubtotal)}</span></div>
                <div className="flex justify-between text-gray-300"><span>Shipping</span><span>{money(shipDisplay)}</span></div>
                <div className="flex justify-between text-white font-display text-2xl pt-2"><span>Total</span><span className="text-emerald-400">{money(totalDisplay)}</span></div>
              </div>

              <div className="mt-6 space-y-3">
                <button onClick={payWithPaystack} disabled={loading} className="w-full btn-skew bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-accent text-xl tracking-wide py-3 glow-green disabled:opacity-60" data-testid="pay-paystack-btn">
                  <span className="flex items-center justify-center gap-2"><CreditCard size={18} /> Pay with Paystack</span>
                </button>
                <button onClick={orderViaWhatsApp} disabled={loading} className="w-full btn-skew bg-[#25D366] hover:bg-[#20bd5a] text-black font-accent text-xl tracking-wide py-3 disabled:opacity-60" data-testid="pay-whatsapp-btn">
                  <span className="flex items-center justify-center gap-2"><MessageCircle size={18} /> Order via WhatsApp</span>
                </button>
                {!config.paystack_enabled && (
                  <p className="text-xs text-amber-400/80 text-center" data-testid="paystack-notice">Card payment is being activated. WhatsApp checkout is available now.</p>
                )}
              </div>
              <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500 mt-4"><ShieldCheck size={13} /> Secure checkout · Prices shown in your local currency ({currency})</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Input = ({ label, value, onChange, type = "text", testid }) => (
  <label className="block">
    <span className="text-sm text-gray-400">{label}</span>
    <input type={type} value={value} onChange={onChange} data-testid={testid}
      className="w-full mt-1 bg-white/5 border border-white/15 rounded-md px-4 py-3 text-white focus:outline-none focus:border-emerald-400" />
  </label>
);
